import type { CSSProperties, ComponentProps } from 'react';
import styled from 'styled-components';

import type { HexColor } from 'style/types';

import { Bezel } from '../Bezel';

const Root = styled.div`
  position: relative;
  isolation: isolate;
  &.disabled {
    border-radius: var(--intermediate-unit);
    border: 1px solid var(--c-nd-150);
    overflow: hidden;
  }
`;
const Triangle = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: var(--width);
  aspect-ratio: 1;
  filter: drop-shadow(0 0 2px var(--color));
  &:after {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    --r: 1px; /* border radius */
    border-top-left-radius: var(--r);
    clip-path: polygon(0 100%, 100% 0, 0 0);
    --_g: calc(50% * (1 + 1 / tan(22.5deg)) - var(--r) / (3 * sqrt(2) - 4));
    --_r: calc(var(--r) / tan(22.5deg)), #000 100%, #0000 100%;
    mask: radial-gradient(var(--r) at top var(--r) right var(--_r)),
      radial-gradient(var(--r) at left var(--r) bottom var(--_r)),
      conic-gradient(from -67.5deg at var(--_g) var(--_g), #000 45deg, #0000 0);
    background: var(--color);
    content: '';
  }
`;

type Props = {
  color?: HexColor;
  disabled?: boolean;
} & ComponentProps<typeof Bezel>;
export const LightedBezel = ({
  bezelSize,
  color,
  background,
  disabled,
  borderColor = `linear-gradient(135deg, ${color}, var(--c-nd-100) 15%)`,
  ...props
}: Props) => {
  if (disabled) {
    return <Root className="disabled">{props.children}</Root>;
  }
  return (
    <Root>
      <Triangle
        style={
          {
            '--width': `${(bezelSize || 16) - 4}px`,
            '--color': color,
          } as CSSProperties
        }
      />
      <Bezel
        {...props}
        borderColor={borderColor}
        background={`linear-gradient(135deg, rgba(0,0,0, 0.6), rgba(0,0,0, 0.6) 15%), linear-gradient(135deg, ${color}, var(--c-nd-100) 15%)${background ? `, ${background}` : ''}`}
      />
    </Root>
  );
};
